/* eslint-disable complexity */
import React, { useCallback } from 'react';
import Button from 'carbon-react/lib/components/button';
import Pill from 'carbon-react/lib/components/pill';
import Typography from 'carbon-react/lib/components/typography';
import { LoaderSpinner } from 'carbon-react/lib/components/loader-spinner';

import { FormattedMessage, getTimestampRelativeFormat, useIntl } from '@futrli/localisation';
import { useSyncContext, SyncControlDatasourceState, useIsExcelOrganisation } from '@futrli/predict';
import { FINANCIAL_INTEGRATION } from '@futrli/predict/core/types';

import { trackCTA } from '../../../../core/tracker';

export const SyncDataButton = () => {
  const {
    workspaceId,
    datasource,
    organisation,
    datasourceState,
    openDrawer,
    syncTimestamp,
    financialConfig,
    integration
  } = useSyncContext();

  const intl = useIntl();
  const isExcelOrganisation = useIsExcelOrganisation();

  const onClickOpenMenu = useCallback(() => {
    openDrawer();
    trackCTA('Datasource Sync Menu', 'Open datasource sync menu');
  }, [openDrawer]);

  if (!datasource || !organisation || !workspaceId) {
    return null;
  }

  const isSyncInProgress = datasourceState === SyncControlDatasourceState.SYNCING;
  const isSyncComplete = datasourceState === SyncControlDatasourceState.SYNC_COMPLETE;
  const isSyncFailed = datasourceState === SyncControlDatasourceState.SYNC_FAILED;
  const isPartiallySynced =
    integration?.slug === FINANCIAL_INTEGRATION.xero &&
    typeof financialConfig?.unreconciled_transactions_count === 'number' &&
    financialConfig?.unreconciled_transactions_count > 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <Button onClick={onClickOpenMenu} data-testid="datasource-button" buttonType="primary" size="small">
        {isSyncInProgress ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <FormattedMessage id="predict.syncing" defaultMessage="Syncing..." />
            <LoaderSpinner size="extra-small" showSpinnerLabel={false} variant="inverse" />
          </span>
        ) : isExcelOrganisation ? (
          <FormattedMessage id="predict.update_data" defaultMessage="Update data" />
        ) : (
          <FormattedMessage id="predict.sync_data" defaultMessage="Sync data" />
        )}
      </Button>
      {isSyncInProgress ? null : isSyncComplete ? (
        <div>
          <Typography variant="p" mb="0" color="rgba(0, 0, 0, 0.55)">
            <FormattedMessage
              id="common.synced_at_relative_time"
              defaultMessage="Synced {relativeTime}"
              values={{
                relativeTime: getTimestampRelativeFormat(syncTimestamp, Date.now(), 1, false, {
                  sentenceCase: true
                })
              }}
            />
          </Typography>
        </div>
      ) : isSyncFailed ? (
        <Pill pillRole="status" size="M" colorVariant="negative" fill>
          {intl.formatMessage({ id: 'common.sync_failed.full', defaultMessage: 'Sync failed' })}
        </Pill>
      ) : isPartiallySynced ? (
        <Pill pillRole="status" size="M" colorVariant="information" fill>
          {intl.formatMessage({ id: 'common.partial_sync', defaultMessage: 'Partial sync' })}
        </Pill>
      ) : null}
    </div>
  );
};
